/**
 * The one way to call the server. Same-origin, cookie session, JSON in and out.
 * A 401 is broadcast so the shell can send the user to login wherever it happened.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export const UNAUTHORIZED_EVENT = 'self-portal:unauthorized';

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

function parse(text: string): unknown {
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function messageOf(body: unknown, status: number): string {
  if (typeof body === 'object' && body !== null && typeof (body as { message?: unknown }).message === 'string') {
    return (body as { message: string }).message;
  }
  return `Request failed with status ${status}`;
}

export interface Api {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  delete<T>(path: string, body?: unknown): Promise<T>;
}

export function createApi(fetchImpl: typeof fetch, onUnauthorized: () => void = () => undefined): Api {
  async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
    const response = await fetchImpl(path, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? { accept: 'application/json' } : { accept: 'application/json', 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = parse(await response.text());
    if (response.status === 401) onUnauthorized();
    if (!response.ok) throw new ApiError(response.status, data, messageOf(data, response.status));
    return data as T;
  }
  return {
    get: (path) => request('GET', path),
    post: (path, body) => request('POST', path, body),
    put: (path, body) => request('PUT', path, body),
    delete: (path, body) => request('DELETE', path, body),
  };
}

export const api: Api = createApi(
  (input, init) => fetch(input, init),
  () => {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
  },
);
